import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { Fund } from "../model/domain/Fund";
import { FundAccount } from "../model/domain/FundAccount";
import { InvestedFundAccount } from "./../model/domain/InvestedFundAccount";
import { StructuredOperationOrderService } from "./tickets/StructuredOperationOrder.service";

import { PublicSecurity } from "../model/domain/PublicSecurity";
import { StatusDetail } from "../model/domain/Summary";
import { NamedType } from "../model/domain/Tipo";
import { ContractedService } from "./tickets/Contracted.service";
import { CurrencyTermService } from "./tickets/CurrencyTerm.service";
import { ExchangeService } from "./tickets/Exchange.service";
import { FundQuotaService } from "./tickets/FundQuota.service";
import { FuturesService } from "./tickets/Futures.service";
import { LoanService } from "./tickets/Loan.service";
import { MarginService } from "./tickets/Margin.service";
import { PrivateFixedIncomeService } from "./tickets/PrivateFixedIncome.service";
import { PublicFixedIncomeService } from "./tickets/PublicFixedIncome.service";
import { SwapCetipService } from "./tickets/SwapCetip.service";
import { TransferService } from "./tickets/Transfer.service";
import { TransferOrderService } from "./tickets/TransferOrder.service";
import { VariableIncomeService } from "./tickets/VariableIncome.service";

// TODO: migrar chamas pro serviço de domínio
import {
  AssetCode,
  CounterpartType,
  DocumentType,
  InterestType,
  MarginSettlmentType,
  MarketType,
  OperationType,
  OperationTypeQuota,
  SettlementType,
} from "../model/domain/Entity";
import { InvestedFund } from "../model/domain/InvestedFund";
import {
  MateraStatementResponse,
  MateraTransferReleaseResponse,
} from "../model/domain/Matera";
import { ShareholderAccountModel } from "../model/domain/ShareholderAccountModel";
import { ShareholderModel } from "../model/domain/ShareholderModel";
import { ExpensesService } from "./tickets/Expenses.service";
import { LiabilitiesService } from "./tickets/Liabilities.service";

import { ClassEnum } from "../enum/class.enum";
import { SerieEnum } from "../enum/series.enum";
import { AcquiredFund } from "../model/domain/AcquiredFund";
import { AcquiredFundAccount } from "../model/domain/AcquiredFundAccount";
import { Counterpart } from "../model/domain/Counterpart";
import { Favored } from "../model/domain/Favored";
import { StructuredOperationsService } from "./tickets/StructuredOperations.service";
@Injectable({
  providedIn: "root",
})
export class TicketService {
  constructor(
    private http: HttpClient,
    public Contracted: ContractedService,
    public FundQuota: FundQuotaService,
    public Futures: FuturesService,
    public Margin: MarginService,
    public PrivateFixedIncome: PrivateFixedIncomeService,
    public PublicFixedIncome: PublicFixedIncomeService,
    public SwapCETIP: SwapCetipService,
    public CurrencyTerm: CurrencyTermService,
    public VariableIncome: VariableIncomeService,
    public TransferOrder: TransferOrderService,
    public Transfer: TransferService,
    public Loan: LoanService,
    public Exchange: ExchangeService,
    public Expenses: ExpensesService,
    public Liabilities: LiabilitiesService,
    public StructuredOperations: StructuredOperationsService,
    public StructuredOperationOrder: StructuredOperationOrderService
  ) {}

  summary(startDate: Date = null, endDate: Date = null) {
    const params: any = {};

    if (startDate) {
      params.startDate = startDate.toJSON();
    }

    if (endDate) {
      params.endDate = endDate.toJSON();
    }

    return this.http.get<StatusDetail[]>(
      `${environment.apiUrls.tickets}/ticket/summary`,
      { params }
    );
  }

  getPublicSecurity(securityName: string = null, showExpired: boolean = false) {
    const params: any = {};

    if (securityName) {
      params.securityName = securityName;
    }

    if (showExpired) {
      params.showExpired = showExpired;
    }

    return this.http.get<PublicSecurity[]>(
      `${environment.apiUrls.tickets}/ticket/public-security`,
      { params }
    );
  }

  getPublicSecurityTypes() {
    return this.http.get<string[]>(
      `${environment.apiUrls.tickets}/ticket/public-security-types`
    );
  }

  Funds(name: string = null, document: string = null): Observable<Fund[]> {
    const params: any = {};

    if (name) {
      params.name = name;
    }

    if (document) {
      params.document = document;
    }

    return this.http.get<Fund[]>(
      `${environment.apiUrls.tickets}/ticket/funds`,
      { params }
    );
  }

  AcquiredFunds(
    name: string = null,
    document: string = null,
    sourceId: string = null
  ): Observable<AcquiredFund[]> {
    const params: any = {};

    if (name) {
      params.name = name;
    }

    if (document) {
      params.document = document;
    }

    if (sourceId) {
      params.sourceId = sourceId;
    }

    return this.http.get<AcquiredFund[]>(
      `${environment.apiUrls.tickets}/ticket/acquired-funds`,
      { params }
    );
  }

  getFundAccount(id: number): Observable<FundAccount[]> {
    return this.http.get<FundAccount[]>(
      `${environment.apiUrls.tickets}/ticket/fundAccount/${id}`
    );
  }

  getFundsByAccountManagerIdList(
    accountManagerIdList: number[]
  ): Observable<Fund[]> {
    const params: any = {};

    if (accountManagerIdList) {
      params.accountManagerIdList = accountManagerIdList;
    }

    return this.http.get<Fund[]>(
      `${environment.apiUrls.tickets}/ticket/funds-by-account-manager-list`,
      { params }
    );
  }

  getInvestedFunds(
    name: string = null,
    document: string = null,
    scheme: string = null,
    sourceId: string = null,
    onlyInternalFunds = false
  ): Observable<InvestedFund[]> {
    const params: any = {};

    if (name) {
      params.name = name;
    }

    if (scheme) {
      params.scheme = scheme;
    }

    if (sourceId) {
      params.sourceId = sourceId;
    }

    if (document) {
      params.document = document;
    }

    if (onlyInternalFunds) {
      params.onlyInternalFunds = onlyInternalFunds;
    }

    return this.http.get<InvestedFund[]>(
      `${environment.apiUrls.tickets}/ticket/invested-funds`,
      { params }
    );
  }

  listClassEnum(): NamedType<number>[] {
    return [
      NamedType.Create(ClassEnum.Senior, "Sênior"),
      NamedType.Create(ClassEnum.SubordinadaJunior, "Subordinada Mezanino"),
      NamedType.Create(ClassEnum.SubordinadaMezanino, "Subordinada Júnior"),
      NamedType.Create(ClassEnum.NaoAplicavel, "Não Aplicável"),
    ];
  }

  listSerieEnum(): NamedType<number>[] {
    return [
      NamedType.Create(SerieEnum.Unique, "Única"),
      NamedType.Create(SerieEnum.Many, "Variada"),
    ];
  }

  getCounterpart(
    name: string = null,
    document: string = null
  ): Observable<Counterpart[]> {
    const params: any = {};

    if (name) {
      params.name = name;
    }

    if (document) {
      params.document = document;
    }

    return this.http.get<Counterpart[]>(
      `${environment.apiUrls.tickets}/ticket/counterpart`,
      { params }
    );
  }

  getFavored(
    name: string = null,
    document: string = null
  ): Observable<Favored[]> {
    const params: any = {};

    if (name) {
      params.name = name;
    }

    if (document) {
      params.document = document;
    }

    return this.http.get<Favored[]>(
      `${environment.apiUrls.tickets}/ticket/counterpart`,
      { params }
    );
  }

  getInvestedFundAccounts(sourceId: string): Observable<InvestedFundAccount[]> {
    const params: any = {};

    if (sourceId) {
      params.sourceId = sourceId;
    }

    return this.http.get<InvestedFundAccount[]>(
      `${environment.apiUrls.tickets}/ticket/invested-fund-accounts`,
      { params }
    );
  }

  getAcquiredFundAccounts(sourceId: string): Observable<AcquiredFundAccount[]> {
    const params: any = {};

    if (sourceId) {
      params.sourceId = sourceId;
    }

    return this.http.get<AcquiredFundAccount[]>(
      `${environment.apiUrls.tickets}/ticket/acquired-fund-accounts`,
      { params }
    );
  }

  getInvestedFundSovereign() {
    return this.http.get<InvestedFund>(
      `${environment.apiUrls.tickets}/ticket/invested-fund-sovereign`
    );
  }

  getShareholders(
    name: string = null,
    document: string = null
  ): Observable<ShareholderModel[]> {
    const params: any = {};

    if (name) {
      params.name = name;
    }

    if (document) {
      params.document = document;
    }

    return this.http.get<ShareholderModel[]>(
      `${environment.apiUrls.tickets}/Shareholder`,
      { params }
    );
  }

  getShareholderAccounts(
    shareholderSourceId: string
  ): Observable<ShareholderAccountModel[]> {
    return this.http.get<ShareholderAccountModel[]>(
      `${environment.apiUrls.tickets}/Shareholder/accounts/${shareholderSourceId}`
    );
  }

  getListBanksSettlement() {
    return this.http.get<string[]>(
      `${environment.apiUrls.tickets}/ticket/list-banks-settlement`
    );
  }

  ApiVersion(): Observable<string> {
    return this.http.get<string>(
      `${environment.apiUrls.tickets}/ticket/version`
    );
  }

  approve(ticketId: number, comments: string = "", groupId: number = null) {
    return this.http.post(
      `${environment.apiUrls.tickets}/ticket/${ticketId}/approve`,
      { groupId }
    );
  }

  reject(ticketId: number, comments: string) {
    return this.http.post(
      `${environment.apiUrls.tickets}/ticket/${ticketId}/reject`,
      { comments }
    );
  }

  getHistory(ticketId: number) {
    const endpointUrl = `${environment.apiUrls.tickets}/ticket/${ticketId}/history`;
    return this.http.get<any>(endpointUrl);
  }

  getWorkflowStatus() {
    const endpointUrl = `${environment.apiUrls.workflow}/workflow/status`;
    return this.http.get<any>(endpointUrl);
  }

  getUploadAccountManagersList() {
    return this.http.get(
      `${environment.apiUrls.files}/file-upload/account-managers`
    );
  }

  ForwardQuotaTicket(id: number) {
    return this.http.post<{ statusDescription: string }>(
      `${environment.apiUrls.tickets}/quota/${id}/forward`,
      {}
    );
  }

  checkEletronicTransferTicket(id: number): Observable<any> {
    return this.http.get(
      `${environment.apiUrls.tickets}/ticket/${id}/check-electronic-transfer`
    );
  }

  getEletronicTransferSituationTicket(id: number): Observable<any> {
    return this.http.get(
      `${environment.apiUrls.tickets}/ticket/${id}/electronic-transfer/status`
    );
  }

  getEletronicTransferTimeLimit(): Observable<number> {
    return this.http.get<number>(
      `${environment.apiUrls.tickets}/ticket/electronic-transfer-time-limit`
    );
  }

  getEletronicTransferStatement(
    portfolioAccount: string = null,
    operationDate: any = null
  ) {
    operationDate = operationDate.split("T")[0];
    const params: any = {
      portfolioAccount,
      operationDate,
    };
    return this.http.get<MateraStatementResponse>(
      `${environment.apiUrls.tickets}/ticket/electronic-transfer-statement`,
      { params }
    );
  }

  getEletronicTransferReleases(
    portfolioAccount: string = null,
    operationDate: any = null
  ) {
    operationDate = operationDate.split("T")[0];
    const params: any = {
      portfolioAccount,
      operationDate,
    };
    return this.http.get<MateraTransferReleaseResponse>(
      `${environment.apiUrls.tickets}/ticket/electronic-transfer-releases`,
      { params }
    );
  }

  export(
    ticketType: number = null,
    startDate: Date = null,
    endDate: Date = null,
    statusId: number = null,
    operationTypeId: number = null,
    settlementTypeId: number = null,
    portfolioCode: string = null,
    portfolioName: string = null,
    portfolioDocument: string = null,
    ticketId: number = null,
    accountManagerId: number = null,
    dateType: number = null
  ) {
    const params: any = {};

    if (ticketType) {
      params.ticketType = ticketType;
    }

    if (startDate) {
      params.startDate = startDate.toJSON();
    }

    if (endDate) {
      params.endDate = endDate.toJSON();
    }

    if (statusId) {
      params.statusId = statusId.toString();
    }

    if (operationTypeId) {
      params.operationTypeId = operationTypeId;
    }

    if (settlementTypeId) {
      params.settlementTypeId = settlementTypeId;
    }

    if (portfolioCode) {
      params.portfolioCode = portfolioCode;
    }

    if (portfolioName) {
      params.portfolioName = portfolioName;
    }

    if (portfolioDocument) {
      params.portfolioDocument = portfolioDocument;
    }

    if (ticketId) {
      params.ticketId = ticketId;
    }

    if (accountManagerId) {
      params.accountManagerId = accountManagerId;
    }

    if (dateType) {
      params.dateType = dateType;
    }

    return this.http.get(`${environment.apiUrls.tickets}/ticket/export`, {
      params: params,
      responseType: "blob",
    });
  }

  apiVersion(): Observable<string> {
    return this.http.get<string>(
      `${environment.apiUrls.tickets}/ticket/version`
    );
  }

  // TODO: migrar chamas pro serviço de domínio (verificar impacto)
  ListStatus(): NamedType<number>[] {
    return [
      NamedType.Create(1, "Em Processamento"),
      NamedType.Create(2, "Não enviada."),
      NamedType.Create(3, "Cancelada"),
      NamedType.Create(4, "Finalizada"),
    ];
  }

  Tickets(): NamedType<number>[] {
    return [
      NamedType.Create(1, "Cotas"),
      NamedType.Create(2, "CETIP"),
      NamedType.Create(3, "SELIC"),
      NamedType.Create(4, "Compromissada de Compra"),
      NamedType.Create(5, "Termo de Moeda"),
      NamedType.Create(6, "RV"),
      NamedType.Create(7, "SwapCetip"),
      NamedType.Create(8, "Futuros"),
      NamedType.Create(9, "Margem"),
    ];
  }

  ListOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Application, "Aplicação"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
      NamedType.Create(OperationType.Deposit, "Deposito"),
      NamedType.Create(OperationType.PurchasedCurrency, "Moeda Comprada"),
      NamedType.Create(OperationType.SoldCurrency, "Moeda Vendida"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
      NamedType.Create(OperationType.Withdrawal, "Retirada"),
      NamedType.Create(OperationType.Transfer, "Transferencia"),
    ];
  }

  ListOperationTypesQuotas(
    shouldReturnAmorization = false
  ): NamedType<number>[] {
    const arrayOperationTypes = [
      NamedType.Create(OperationTypeQuota.Application, "Aplicação"),
      NamedType.Create(OperationTypeQuota.Redemption, "Resgate"),
      NamedType.Create(OperationTypeQuota.FullRedemption, "Resgate Total"),
    ];

    if (shouldReturnAmorization) {
      arrayOperationTypes.push(
        NamedType.Create(
          OperationTypeQuota.Amortization,
          "Recebimento de Amortização"
        )
      );
    }

    return arrayOperationTypes;
  }

  ListOperationTypesLiabilities(): NamedType<number>[] {
    const arrayOperationTypes = [
      NamedType.Create(OperationType.Application, "Aplicação"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
      NamedType.Create(OperationType.FullRedemption, "Resgate Total"),
    ];

    return arrayOperationTypes;
  }

  ListSettlementTypesLiabilities(): NamedType<number>[] {
    const listItens = [
      NamedType.Create(SettlementType.CETIP, "CETIP"),
      NamedType.Create(SettlementType.TED, "TED/TEF"),
    ];

    return listItens;
  }

  ListQuotaOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Application, "Aplicação"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
      NamedType.Create(OperationType.Transfer, "Transferência"),
    ];
  }

  ListSettlementTypesQuotas(active: boolean): NamedType<number>[] {
    const listItens = [
      NamedType.Create(SettlementType.CETIP, "CETIP"),
      NamedType.Create(SettlementType.TED, "TED/TEF"),
      NamedType.Create(SettlementType.NoFinancial, "Sem Financeiro"),
    ];

    if (active) {
      listItens.push(
        NamedType.Create(SettlementType.Sovereign, "Soberano Santander")
      );
    }

    return listItens;
  }

  ListQuotaSettlementTypes(): NamedType<number>[] {
    return [
      NamedType.Create(SettlementType.CETIP, "CETIP"),
      NamedType.Create(SettlementType.TED, "TED"),
    ];
  }

  ListExpensesSettlementTypes(): NamedType<number>[] {
    return [
      NamedType.Create(SettlementType.TED, "TED/TEF"),
      NamedType.Create(SettlementType.BankSlip, "Boleto Bancário"),
    ];
  }

  ListCetipSettlementTypes(): NamedType<number>[] {
    return [
      NamedType.Create(SettlementType.CETIP, "CETIP"),
      NamedType.Create(SettlementType.TED, "TED/TEF"),
      NamedType.Create(SettlementType.NoFinancial, "Sem Financeiro"),
    ];
  }

  ListStructuredOrderNoFinancialSettlementTypes(): NamedType<number>[] {
    return [
      NamedType.Create(SettlementType.CETIP, "CETIP"),
      NamedType.Create(SettlementType.TED, "TED/TEF"),
    ];
  }

  ListQuotaNoFinancialOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Deposit, "Depósito"),
      NamedType.Create(OperationType.Withdrawal, "Retirada"),
    ];
  }
  ListSelicSettlementTypes(): NamedType<number>[] {
    return [
      NamedType.Create(SettlementType.Term, "Termo"),
      NamedType.Create(SettlementType.InCash, "À vista"),
    ];
  }

  ListTypeOfDocument(): NamedType<number>[] {
    return [
      NamedType.Create(DocumentType.CNPJ, "Pessoa Jurídica"),
      NamedType.Create(DocumentType.CPF, "Pessoa Física"),
    ];
  }

  ListStandardOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Application, "Aplicação"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
    ];
  }

  ListStructuredOrderOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Application, "Compra"),
      NamedType.Create(OperationType.Redemption, "Venda"),
    ];
  }

  ListStandardOperationTypesAndFullRedemption(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Application, "Aplicação"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
      NamedType.Create(OperationType.FullRedemption, "Resgate Total"),
    ];
  }

  ListContractedOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Application, "Compra"),
      NamedType.Create(OperationType.Redemption, "Resgate"),
      NamedType.Create(OperationType.FullRedemption, "Resgate Total"),
    ];
  }

  ListCetipNoFinancialOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Deposit, "Depósito"),
      NamedType.Create(OperationType.Withdrawal, "Retirada"),
    ];
  }

  ListMarginOperationTypes(): NamedType<number>[] {
    return [
      NamedType.Create(OperationType.Deposit, "Depósito (Vinculação)"),
      NamedType.Create(OperationType.Withdrawal, "Retirada (Desvinculação)"),
    ];
  }

  ListTransferOperationTypes(): NamedType<number>[] {
    return [NamedType.Create(OperationType.TED, "TED/TEF")];
  }

  ListCounterparTypes(): NamedType<number>[] {
    return [
      NamedType.Create(CounterpartType.PessoaJuridica, "Pessoa Jurídica"),
      NamedType.Create(CounterpartType.PessoaFisica, "Pessoa Física"),
    ];
  }

  ListAssetCode(): NamedType<number>[] {
    return [
      NamedType.Create(AssetCode.CDB, "CDB"),
      NamedType.Create(AssetCode.LF, "LF"),
      NamedType.Create(AssetCode.LCI, "LCI"),
      NamedType.Create(AssetCode.LCA, "LCA"),
      NamedType.Create(AssetCode.RDB, "RDB"),
      NamedType.Create(AssetCode.DEB, "DEB"),
      NamedType.Create(AssetCode.CRI, "CRI"),
      NamedType.Create(AssetCode.CRA, "CRA"),
      NamedType.Create(AssetCode.CPR, "CPR"),
      NamedType.Create(AssetCode.CCI, "CCI"),
      NamedType.Create(AssetCode.CCE, "CCE"),
      NamedType.Create(AssetCode.CCB, "CCB"),
      NamedType.Create(AssetCode.DPGE, "DPGE"),
      NamedType.Create(AssetCode.LC, "LC"),
      NamedType.Create(AssetCode.NC, "NC"),
    ].sort((a, b) => a.name.localeCompare(b.name));
  }

  ListMarketTypes(): NamedType<number>[] {
    return [
      NamedType.Create(
        MarketType.FixedIncomePrivateAsset,
        "RF - Título Privado"
      ),
      NamedType.Create(
        MarketType.FixedIncomeGovernmentBond,
        "RF - Título Público"
      ),
      NamedType.Create(MarketType.VariableIncome, "Renda Variável"),
      NamedType.Create(MarketType.Cash, "Dinheiro"),
    ];
  }

  ListMarginSettlementTypes(): NamedType<number>[] {
    return [
      NamedType.Create(MarginSettlmentType.SELIC, "SELIC"),
      NamedType.Create(MarginSettlmentType.B3, "B3"),
      NamedType.Create(MarginSettlmentType.Cash, "Dinheiro"),
    ];
  }

  ListFoundationIndexes(): NamedType<number>[] {
    return [NamedType.Create(1, "252 dias"), NamedType.Create(2, "360 dias")];
  }

  ListInterestTypes(): NamedType<number>[] {
    return [
      NamedType.Create(InterestType.Exponencial, "Exponencial"),
      NamedType.Create(InterestType.Linear, "Linear"),
    ];
  }

  ListExpenses() {
    const endpointUrl = `${environment.apiUrls.tickets}/expenses/list-Expenses`;
    return this.http.get<any>(endpointUrl);
  }
}
